import { cva } from "class-variance-authority"
import PropTypes from "prop-types"
import { forwardRef } from "react"

import { cn } from "styles/utils"

const labelVariants = cva(
  "focus-within:outline-primary inline-flex cursor-pointer select-none items-center gap-8 focus-within:outline-2 focus-within:outline-offset-2",
  {
    defaultVariants: {
      size: "sm",
    },
    variants: {
      disabled: {
        false: "",
        true: "cursor-not-allowed opacity-50",
      },
      size: {
        lg: "text-22",
        md: "text-18",
        sm: "text-14",
      },
    },
  }
)

const radioVariants = cva(
  "relative shrink-0 rounded-full transition-all duration-300",
  {
    compoundVariants: [
      {
        checked: true,
        class: "bg-primary border-primary",
        variant: "default",
      },
      {
        checked: true,
        class: "after:bg-primary after:border-primary before:block",
        variant: "check",
      },
    ],
    defaultVariants: {
      size: "sm",
      variant: "default",
    },
    variants: {
      checked: {
        false: "",
        true: "",
      },
      size: {
        lg: "inset-ring-6 h-32 w-32",
        md: "inset-ring-5 h-24 w-24",
        sm: "inset-ring-3 h-16 w-16",
      },
      variant: {
        check:
          "before:absolute before:left-6 before:top-3 before:z-10 before:hidden before:h-8 before:w-4 before:rotate-45 before:border-b-2 before:border-r-2 before:border-white after:absolute after:h-16 after:w-16 after:rounded-full after:border after:border-black after:bg-white",
        default: "border-grey-20 inset-ring-white border bg-transparent",
      },
    },
  }
)

const Radio = forwardRef(
  (
    {
      checked = false,
      children,
      className,
      disabled = false,
      labelClassName,
      size = "sm",
      variant = "default",
      ...props
    },
    ref
  ) => {
    return (
      <label
        className={cn(
          labelVariants({
            className: labelClassName,
            disabled,
            size,
          })
        )}
      >
        <input
          checked={checked}
          className="sr-only"
          disabled={disabled}
          ref={ref}
          type="radio"
          {...props}
        />
        <span
          className={cn(
            radioVariants({
              checked,
              className,
              size,
              variant,
            })
          )}
        />
        {children}
      </label>
    )
  }
)

Radio.displayName = "Radio"

Radio.propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  labelClassName: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  variant: PropTypes.oneOf(["default", "check"]),
}

export default Radio
